// identity/identity.js: M3-7 localStorage-backed account stub.
//
// Per brief: "account stub stored in localStorage with a generated UUID
// so it can later migrate to a real account without rewriting the
// identity layer." Cross-cutting, room UI, room model, and any later
// account system all depend on this shape.
//
// See identity-migration.md for the post-MVP real-account migration
// path.

import { generateUUIDv4 } from './uuid.js';
import { AVATAR_COLORS, DEFAULT_AVATAR_COLOR, isAvatarColorKey } from './avatar-colors.js';

export const STORAGE_KEY = 'euchredev_identity_v1';
export const SCHEMA_VERSION = 1;
export const NAME_MAX = 20;

/**
 * @typedef {import('./avatar-colors.js').AvatarColorKey} AvatarColorKey
 *
 * @typedef {{
 *   client_uuid: string,
 *   display_name: string,
 *   avatar_color: AvatarColorKey,
 *   schema_version: 1,
 * }} Identity
 */

function getStorage() {
  /** @type {any} */
  const g = globalThis;
  if (g && g.localStorage) return /** @type {Storage} */ (g.localStorage);
  throw new Error('identity: localStorage is not available in this environment');
}

/**
 * @param {string} uuid
 * @returns {string}
 */
function defaultDisplayName(uuid) {
  return 'Guest_' + uuid.slice(0, 4).toUpperCase();
}

/**
 * Coerce a candidate display name into the persisted shape:
 * - trim whitespace
 * - cap at NAME_MAX characters
 * - reject empty (fall back to a Guest_XXXX default derived from uuid)
 *
 * @param {unknown} candidate
 * @param {string} uuid
 * @returns {string}
 */
export function normalizeDisplayName(candidate, uuid) {
  const raw = typeof candidate === 'string' ? candidate : '';
  const trimmed = raw.trim();
  if (trimmed.length === 0) return defaultDisplayName(uuid);
  return trimmed.slice(0, NAME_MAX);
}

/**
 * @returns {Identity}
 */
function buildFresh() {
  const uuid = generateUUIDv4();
  return {
    client_uuid: uuid,
    display_name: defaultDisplayName(uuid),
    avatar_color: DEFAULT_AVATAR_COLOR,
    schema_version: SCHEMA_VERSION,
  };
}

/**
 * @param {unknown} raw
 * @returns {Identity|null}
 */
function parseStored(raw) {
  if (typeof raw !== 'string' || raw.length === 0) return null;
  let parsed;
  try { parsed = JSON.parse(raw); } catch { return null; }
  if (!parsed || typeof parsed !== 'object') return null;
  /** @type {any} */
  const p = parsed;
  if (typeof p.client_uuid !== 'string' || p.client_uuid.length === 0) return null;
  if (p.schema_version !== SCHEMA_VERSION) return null;
  const color = isAvatarColorKey(p.avatar_color) ? p.avatar_color : DEFAULT_AVATAR_COLOR;
  return {
    client_uuid:    p.client_uuid,
    display_name:   normalizeDisplayName(p.display_name, p.client_uuid),
    avatar_color:   color,
    schema_version: SCHEMA_VERSION,
  };
}

/**
 * Read the persisted identity, or generate + persist a fresh one if
 * none exists (or the stored value is corrupt / outdated).
 *
 * @returns {Identity}
 */
export function getOrCreateIdentity() {
  const storage = getStorage();
  const raw = storage.getItem(STORAGE_KEY);
  const existing = parseStored(raw);
  if (existing) return existing;
  const fresh = buildFresh();
  storage.setItem(STORAGE_KEY, JSON.stringify(fresh));
  return fresh;
}

/**
 * Merge a patch into the persisted identity. Unknown keys are ignored.
 * client_uuid and schema_version are never overwritten.
 *
 * @param {Partial<{ display_name: string, avatar_color: AvatarColorKey }>} patch
 * @returns {Identity}
 */
export function updateIdentity(patch) {
  const current = getOrCreateIdentity();
  /** @type {Identity} */
  const next = { ...current };
  if (patch && typeof patch === 'object') {
    if ('display_name' in patch) {
      next.display_name = normalizeDisplayName(patch.display_name, current.client_uuid);
    }
    if ('avatar_color' in patch && typeof patch.avatar_color === 'string'
        && isAvatarColorKey(patch.avatar_color)) {
      next.avatar_color = patch.avatar_color;
    }
  }
  const storage = getStorage();
  storage.setItem(STORAGE_KEY, JSON.stringify(next));
  return next;
}

export const _internals = {
  AVATAR_COLORS,
  defaultDisplayName,
  parseStored,
};
